import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, isDirectivo, isPatient, isStaff, isSecretaria, isExternalPro, isEntityDirector } from "@/lib/api";
import { coordSectionsFor } from "@/lib/coordSections";
import {
    LayoutDashboard, Calendar, Users, Stethoscope,
    Receipt, Tag, BarChart3, LogOut, UserCircle2, Palmtree, ClipboardCheck, ClipboardList, Signature, MessageSquare, FileCheck,
    ListChecks, Building2,
    Menu, X, ChevronDown, ChevronRight, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import ProfileDialog from "@/components/ProfileDialog";
import NotificationsBell from "@/components/NotificationsBell";

// Grupo "Ausencias" con subitems (permisos por rol se filtran en render)
const ABSENCES_GROUP = {
    id: "absences-group",
    label: "Ausencias",
    icon: Palmtree,
    children: [
        { to: "/vacaciones", label: "Vacaciones", id: "vacation-calendar", roles: ["directivo", "coordinadora", "coordination_perm"] },
        { to: "/vacaciones-global", label: "Vacaciones Global", id: "vacation-global", roles: ["directivo", "coordinadora", "secretaria", "coordination_perm"] },
    ],
};

// [FDE feb-2026] Grupo "Pacientes" con subitem Entidades colaboradoras (solo directivo).
// El sub-ítem "Pacientes" lleva al listado clásico (/pacientes). El sub-ítem
// "Entidades colaboradoras" lleva a la gestión del CRUD de entidades.
const PATIENTS_GROUP = {
    id: "patients-group",
    label: "Pacientes",
    icon: Users,
    children: [
        { to: "/pacientes", label: "Pacientes", id: "patients", end: true, roles: ["directivo", "staff", "directora_entidad"] },
        { to: "/pacientes/grupos", label: "Grupos", id: "patient-groups", roles: ["directivo", "staff"] },
        { to: "/pacientes/entidades", label: "Entidades colaboradoras", id: "entities", roles: ["directivo"] },
    ],
};

// Grupo "Facturación": cada rol solo ve los subapartados a los que ya tenía acceso.
const BILLING_GROUP = {
    id: "billing-group",
    label: "Facturación",
    icon: Receipt,
    children: [
        { to: "/facturacion", label: "Facturas", id: "billing", end: true, roles: ["directivo"] },
        { to: "/facturacion/configuracion", label: "Config. facturación", id: "billing-settings", roles: ["directivo"] },
        { to: "/mis-facturas", label: "Mis facturas", id: "my-invoices", roles: ["plus_verifactu"] },
        { to: "/archivo-gastos", label: "Archivo de gastos", id: "expenses-archive", roles: ["directivo"] },
    ],
};

// Grupo "Profesionales": cada rol solo ve los subapartados a los que ya tenía acceso.
const PROFESSIONALS_GROUP = {
    id: "professionals-group",
    label: "Profesionales",
    icon: Stethoscope,
    children: [
        { to: "/profesionales", label: "Activos", id: "professionals", roles: ["directivo"] },
        { to: "/mi-ficha", label: "Mi ficha profesional", id: "my-card", roles: ["directivo", "staff"] },
        { to: "/fichaje", label: "Fichaje", id: "timeclock", roles: ["directivo", "staff", "coordination_perm"] },
        { to: "/ausencias", label: "Mis ausencias", id: "absences", roles: ["directivo", "staff"] },
    ],
};

const ALL_NAV = [
    { to: "/", label: "Panel", icon: LayoutDashboard, end: true, id: "dashboard", roles: ["directivo"] },
    { to: "/entidad", label: "Mi entidad", icon: Building2, id: "entity-panel", roles: ["directora_entidad"] },
    { to: "/calendario", label: "Agenda", icon: Calendar, id: "calendar", roles: ["directivo", "staff", "directora_entidad"] },
    PATIENTS_GROUP,
    { id: "coordination-group", label: "Coordinación", icon: ClipboardCheck, roles: ["directivo", "staff"], coordSections: true },
    { to: "/asistencias", label: "Asistencias", icon: ClipboardList, id: "attendance", roles: ["directivo", "coordinadora", "coordination_perm", "staff", "profesional", "directora_entidad"] },
    { to: "/mensajeria", label: "Mensajería", icon: MessageSquare, id: "messaging", roles: ["directivo", "coordinadora", "coordination_perm"] },
    PROFESSIONALS_GROUP,
    ABSENCES_GROUP,
    BILLING_GROUP,
    { to: "/mis-tareas", label: "Mis tareas", icon: ListChecks, id: "my-tasks", roles: ["directivo", "staff"] },
    { to: "/tarifas", label: "Tarifas", icon: Tag, id: "tariffs", roles: ["directivo", "coordinadora", "coordination_perm"] },
    { to: "/documentos-legales", label: "Documentos legales", icon: FileCheck, id: "legal-docs-manager", roles: ["directivo"] },
    { to: "/estadisticas", label: "Estadísticas", icon: BarChart3, id: "stats", roles: ["directivo"] },
    { to: "/mi-cuenta", label: "Mi cuenta", icon: UserCircle2, id: "portal", roles: ["paciente"] },
];

function isChildActive(c, path) {
    return c.end ? path === c.to : path === c.to || path.startsWith(`${c.to}/`);
}

function roleAllows(user, roles) {
    if (isEntityDirector(user)) return roles.includes("directora_entidad");
    const hasCoordPerm = Array.isArray(user?.extra_permissions) && user.extra_permissions.includes("coordination");
    const hasPlusVerifactu = Array.isArray(user?.extra_permissions) && user.extra_permissions.includes("plus_verifactu");
    // `plus_verifactu` es un permiso individual: si el nav item lo requiere y el
    // usuario lo tiene concedido, se muestra (aplica también a directivos).
    if (roles.includes("plus_verifactu")) return !!hasPlusVerifactu || isDirectivo(user);
    if (isDirectivo(user)) {
        // Secretaría tiene los mismos permisos que directivo EXCEPTO estos ítems (ocultos):
        if (isSecretaria(user)) {
            const HIDDEN_FOR_SECRETARIA = ["messaging", "professionals", "billing-settings", "timeclock"];
            // El nav item se identifica por `roles` (heurística: buscamos IDs comunes en roles del nav).
            // Como no disponemos del id aquí, dejamos la comprobación en `navForUser` (más abajo).
        }
        return roles.includes("directivo");
    }
    if (isPatient(user)) return roles.includes("paciente");
    if (user?.role === "coordinadora") return roles.includes("coordinadora") || roles.includes("staff") || roles.includes("coordination_perm");
    if (hasCoordPerm && roles.includes("coordination_perm")) return true;
    if (isStaff(user)) return roles.includes("staff");
    return false;
}

const ENTITY_DIRECTOR_PATHS = ["/entidad", "/calendario", "/pacientes", "/asistencias", "/change-password"];

// Items ocultos para Secretaría (heredan permisos de directivo pero se ocultan explícitamente)
const HIDDEN_FOR_SECRETARIA = new Set(["billing-settings"]);

function navForUser(user) {
    const isSec = isSecretaria(user);
    // Los profesionales externos no fichan → se les oculta el ítem Fichaje.
    const isExternal = isExternalPro(user);
    const HIDDEN_FOR_EXTERNAL = new Set(["timeclock"]);
    return ALL_NAV.map((n) => {
        if (n.coordSections) {
            // Subapartados de Coordinación según el usuario (externos: solo lo suyo).
            if (!roleAllows(user, n.roles)) return null;
            return { ...n, children: coordSectionsFor(user).map((s) => ({ to: `/coordinacion/${s.key}`, label: s.label, id: `coord-${s.key}` })) };
        }
        if (n.children) {
            const kids = n.children.filter((c) => roleAllows(user, c.roles)
                && !(isSec && HIDDEN_FOR_SECRETARIA.has(c.id))
                && !(isExternal && HIDDEN_FOR_EXTERNAL.has(c.id)));
            return kids.length ? { ...n, children: kids } : null;
        }
        if (isSec && HIDDEN_FOR_SECRETARIA.has(n.id)) return null;
        if (isExternal && HIDDEN_FOR_EXTERNAL.has(n.id)) return null;
        return roleAllows(user, n.roles) ? n : null;
    }).filter(Boolean);
}

export default function Layout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const items = navForUser(user);
    const [profileOpen, setProfileOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [unreadMsgs, setUnreadMsgs] = useState(0);
    // [FDE feb-2026] Notificación de nuevas tareas solicitadas por otros profesionales.
    const [unseenTasks, setUnseenTasks] = useState({ count: 0, items: [] });
    const [notifiedTaskIds] = useState(() => new Set());
    // Open-state por grupo: un clic en el título despliega y otro pliega.
    // Al navegar a una ruta de un grupo, ese grupo se despliega.
    const [openGroups, setOpenGroups] = useState({});
    const toggleGroup = (gid) => setOpenGroups((prev) => ({ ...prev, [gid]: !prev[gid] }));
    const activeGroupId = items.find((n) => n.children?.some((c) => isChildActive(c, loc.pathname)))?.id;
    useEffect(() => {
        if (activeGroupId) setOpenGroups((prev) => (prev[activeGroupId] ? prev : { ...prev, [activeGroupId]: true }));
    }, [activeGroupId, loc.pathname]);

    // Polling ligero del contador de mensajes sin leer para el badge del sidebar.
    useEffect(() => {
        if (!isStaff(user)) return undefined;
        let cancelled = false;
        const fetch = async () => {
            try {
                const { data } = await import("@/lib/api").then((m) => m.api.get("/messages/unread-count"));
                if (!cancelled) setUnreadMsgs(data?.unread || 0);
            } catch (_e) { /* silencioso */ }
        };
        fetch();
        const id = setInterval(fetch, 15000);
        return () => { cancelled = true; clearInterval(id); };
    }, [user, loc.pathname]);

    // [FDE feb-2026] Poller de tareas solicitadas por otros profesionales (no vistas).
    // Muestra badge en el nav "Mis tareas" y lanza toast la PRIMERA vez que vemos
    // una tarea nueva (los ids ya notificados se guardan en memoria para no
    // repetir el toast en siguientes polls). Se para en pantallas donde no aplica.
    useEffect(() => {
        if (!user || isPatient(user) || isEntityDirector(user)) return;
        let cancelled = false;
        let firstCheck = true;
        const fetchUnseen = async () => {
            try {
                const { api } = await import("@/lib/api");
                const { toast } = await import("sonner");
                const { data } = await api.get("/my-tasks/unseen");
                if (cancelled) return;
                setUnseenTasks({ count: data?.count || 0, items: data?.items || [] });
                const updates = (data?.updates || []).map((u) => ({ ...u, key: `${u.id}:${u.status_changed_at}` }));
                // No lanzamos toasts en el primer check tras login/montaje (ya
                // hay un badge visible y evitamos spam al llegar a la app).
                if (!firstCheck) {
                    for (const t of (data?.items || [])) {
                        if (!notifiedTaskIds.has(t.id)) {
                            notifiedTaskIds.add(t.id);
                            const who = t.requested_by_name || "un compañero";
                            const preview = (t.text || "").slice(0, 80);
                            toast.message(`Nueva tarea de ${who}`, {
                                description: preview + ((t.text || "").length > 80 ? "…" : ""),
                                action: {
                                    label: "Ver",
                                    onClick: () => nav("/mis-tareas"),
                                },
                                duration: 10000,
                            });
                        }
                    }
                    // Avisos de tareas que YO solicité: realizada o vuelta a pendiente.
                    for (const u of updates) {
                        if (notifiedTaskIds.has(u.key)) continue;
                        notifiedTaskIds.add(u.key);
                        const who = u.status_changed_by_name || "Un compañero";
                        const preview = (u.text || "").slice(0, 80) + ((u.text || "").length > 80 ? "…" : "");
                        toast.message(u.completed ? `${who} ha completado tu tarea` : `${who} ha vuelto a poner pendiente tu tarea`, {
                            description: preview,
                            action: { label: "Ver", onClick: () => nav("/mis-tareas?tab=sent") },
                            duration: 10000,
                        });
                    }
                } else {
                    // En el primer check, marcamos como "ya notificados" para
                    // que no suene toast al refrescar un segundo después.
                    (data?.items || []).forEach((t) => notifiedTaskIds.add(t.id));
                    updates.forEach((u) => notifiedTaskIds.add(u.key));
                }
                firstCheck = false;
            } catch (_e) { /* silencioso */ }
        };
        fetchUnseen();
        const id = setInterval(fetchUnseen, 20000);
        return () => { cancelled = true; clearInterval(id); };
    }, [user, nav, notifiedTaskIds]);

    // La directora de entidad solo puede navegar por las secciones de su entidad.
    useEffect(() => {
        if (!isEntityDirector(user)) return;
        const p = loc.pathname;
        const ok = ENTITY_DIRECTOR_PATHS.some((x) => p === x || p.startsWith(`${x}/`)) && !p.startsWith("/pacientes/entidades");
        if (!ok) nav("/entidad", { replace: true });
    }, [user, loc.pathname, nav]);

    // Cerrar el drawer al cambiar de ruta
    useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);
    // Bloquear scroll body cuando el drawer está abierto
    useEffect(() => {
        document.body.style.overflow = drawerOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [drawerOpen]);

    const sidebarInner = (
        <>
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
                <NavLink to="/" className="flex items-center gap-2 group" data-testid="sidebar-logo-home">
                    <img
                        src="/logo-boreal.png"
                        alt="EN Boreal"
                        className="w-10 h-10 object-contain shrink-0 transition-transform group-hover:scale-105"
                        draggable={false}
                    />
                    <div className="leading-tight">
                        <div className="font-semibold tracking-tight text-foreground text-sm">EN Boreal</div>
                        <div className="text-[10px] text-muted-foreground">Neurorrehabilitación</div>
                    </div>
                </NavLink>
                <button
                    className="lg:hidden p-2 rounded-md hover:bg-muted"
                    onClick={() => setDrawerOpen(false)}
                    data-testid="sidebar-close"
                    aria-label="Cerrar menú"
                >
                    <X size={18} />
                </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {items.map((n) => {
                    if (n.children) {
                        const anyActive = n.children.some((c) => isChildActive(c, loc.pathname));
                        const isOpen = !!openGroups[n.id];
                        const kids = n.children;
                        return (
                            <div key={n.id}>
                                <button
                                    onClick={() => toggleGroup(n.id)}
                                    aria-expanded={isOpen}
                                    className={`nav-item w-full justify-between ${anyActive ? "text-foreground" : ""}`}
                                    data-testid={`nav-${n.id}`}
                                >
                                    <span className="flex items-center gap-3">
                                        <n.icon size={16} strokeWidth={1.75} /> {n.label}
                                    </span>
                                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </button>
                                {isOpen && (
                                    <div className="ml-4 mt-1 space-y-0.5 border-l border-border/60 pl-3">
                                        {kids.map((c) => (
                                            <NavLink
                                                key={c.to} to={c.to} end={c.end}
                                                data-testid={`nav-${c.id}`}
                                                className={({ isActive }) => `nav-item text-sm ${isActive ? "active" : ""}`}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40 shrink-0" /> {c.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return (
                        <NavLink
                            key={n.to} to={n.to} end={n.end}
                            data-testid={`nav-${n.id}`}
                            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                        >
                            <n.icon size={16} strokeWidth={1.75} />
                            <span className="flex-1">{n.label}</span>
                            {n.id === "messaging" && unreadMsgs > 0 && (
                                <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold" data-testid="nav-messaging-badge">
                                    {unreadMsgs > 99 ? "99+" : unreadMsgs}
                                </span>
                            )}
                            {n.id === "my-tasks" && unseenTasks.count > 0 && (
                                <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-semibold"
                                    data-testid="nav-my-tasks-badge"
                                    title={`${unseenTasks.count} novedad(es): tareas nuevas solicitadas o tareas tuyas completadas/reabiertas`}>
                                    {unseenTasks.count > 99 ? "99+" : unseenTasks.count}
                                </span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>
            <div className="px-3 py-3 border-t border-border">
                <div className="px-3 py-2 mb-1">
                    <div className="text-sm font-medium text-foreground" data-testid="current-user-name">{user?.name}</div>
                    <div className="text-xs text-muted-foreground" data-testid="current-user-role">
                        {ROLE_LABELS[user?.role] || user?.role}
                    </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full justify-start" data-testid="logout-btn"
                    onClick={async () => { await logout(); nav("/login"); }}>
                    <LogOut size={14} className="mr-2" /> Cerrar sesión
                </Button>
                {isStaff(user) && (
                    <Button variant="ghost" size="sm" className="w-full justify-start" data-testid="profile-btn"
                        onClick={() => setProfileOpen(true)}>
                        <Signature size={14} className="mr-2" /> Mi firma / perfil
                    </Button>
                )}
            </div>
        </>
    );

    return (
        <div className="min-h-screen lg:flex">
            {/* Header móvil (hamburguesa) */}
            <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-border flex items-center justify-between px-4 py-3">
                <NavLink to="/" className="flex items-center gap-2" data-testid="mobile-logo-home">
                    <img src="/logo-boreal.png" alt="EN Boreal" className="w-8 h-8 object-contain" draggable={false} />
                    <span className="font-semibold">EN Boreal</span>
                </NavLink>
                <div className="flex items-center gap-1">
                    {isStaff(user) && <NotificationsBell />}
                    <button
                        className="p-2 rounded-md hover:bg-muted"
                        onClick={() => setDrawerOpen(true)}
                        data-testid="sidebar-open"
                        aria-label="Abrir menú"
                    >
                        <Menu size={20} />
                    </button>
                </div>
            </header>

            {/* Sidebar desktop (siempre visible ≥lg) */}
            <aside className="hidden lg:flex w-64 border-r border-border bg-white flex-col shrink-0" data-testid="sidebar">
                {sidebarInner}
            </aside>

            {/* Drawer móvil */}
            {drawerOpen && (
                <>
                    <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setDrawerOpen(false)} data-testid="sidebar-backdrop" />
                    <aside className="lg:hidden fixed top-0 left-0 bottom-0 w-72 bg-white border-r border-border z-50 flex flex-col animate-in slide-in-from-left"
                            data-testid="sidebar-drawer">
                        {sidebarInner}
                    </aside>
                </>
            )}

            <main className="flex-1 min-w-0 bg-background relative">
                {/* [FDE feb-2026] Barra superior: botón "Volver" (desktop) + campana.
                    "Volver" se oculta en el home y login para no mostrarse en pantallas
                    sin ruta anterior lógica. Usa useNavigate(-1) para regresar a la
                    pantalla anterior del historial del navegador. */}
                {isStaff(user) && (
                    <div className="hidden lg:flex items-center justify-between gap-2 px-8 pt-4 -mb-2">
                        {loc.pathname !== "/" && loc.pathname !== "/login" ? (
                            <Button variant="ghost" size="sm" onClick={() => nav(-1)}
                                data-testid="layout-back-btn"
                                className="text-muted-foreground hover:text-foreground">
                                <ArrowLeft size={14} className="mr-1" /> Volver
                            </Button>
                        ) : <span />}
                        <NotificationsBell />
                    </div>
                )}
                {/* [FDE feb-2026] Botón Volver móvil (debajo del header hamburguesa). */}
                {loc.pathname !== "/" && loc.pathname !== "/login" && (
                    <div className="lg:hidden px-4 pt-2">
                        <Button variant="ghost" size="sm" onClick={() => nav(-1)}
                            data-testid="layout-back-btn-mobile"
                            className="text-muted-foreground hover:text-foreground -ml-2">
                            <ArrowLeft size={14} className="mr-1" /> Volver
                        </Button>
                    </div>
                )}
                <Outlet />
            </main>
            <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
        </div>
    );
}
